import { membershipEnd, membershipInYear, membershipStatus } from '../utils/membershipPeriod';
import { datePart, honoraryOnDate, honoraryInYear } from '../utils/honoraryMembership';
import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowDownTrayIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  SparklesIcon,
  ChartPieIcon,
  CalendarDaysIcon,
  ShieldExclamationIcon,
  MapPinIcon,
  CakeIcon,
  TrophyIcon,
  StarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import ExcelJS from 'exceljs';
import GenderIcon from './icons/GenderIcon';
import apiClient from '../utils/apiClient';
import { StatCard, StatGroup } from './StatCard';
import { ModalShell, ModalActions } from './modals/core';
import type { ModalCloseReason } from './modals/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  Area
} from 'recharts';

// Type-safe wrapper for FaDog icon
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface ExportMember {
  kuendigung_am?: string | null;
  austritt_am?: string | null;
  ehrenmitglied_ab?: string | null;
  ehrenmitglied_bis?: string | null;
  mg_nr: number;
  mitglied_nr: string;
  name: string;
  vorname: string;
  geb_am: string | null;
  strasse: string;
  geschlecht: string;
  gender: boolean | null; // false = Male (M), true = Female (W), null = Not specified
  plz: string;
  ort: string;
  tel_nummer: string;
  email: string;
  eintritt: string | null;
  geloescht_am: string | null;
  geloescht: number;
  datenschutzerklaerung: number;
  homepage: number;
  facebook: number;
  presse: number;
}

interface ExportDog {
  name: string;
  versicherung: string;
  tollwut_gueltig_bis: string | null;
  valid_insurance: boolean;
  valid_tollwut: boolean;
  haftpflicht: boolean;
}

interface DogByMember {
  mg_nr: number;
  vorname: string;
  name: string;
  dogs: ExportDog[];
}

interface PaymentByMember {
  kuendigung_am?: string | null;
  austritt_am?: string | null;
  ehrenmitglied_ab?: string | null;
  ehrenmitglied_bis?: string | null;
  mg_nr: number;
  vorname: string;
  name: string;
  mitglied_nr: string;
  eintritt: string | null;
  geloescht_am: string | null;
  geloescht: number;
  payments: Record<number, { betrag: number; buchungstag: string }>;
}

interface Contribution {
  year: number;
  adults: number;
  partner: number;
  youth: number;
}

interface MemberStats {
  year: number;
  eintritte: number;
  austritte: number;
  totalMembers: number;
}

interface ExportData {
  members: ExportMember[];
  dogsByMember: DogByMember[];
  paymentsByMember: PaymentByMember[];
  availableYears: number[];
  contributions: Contribution[];
  memberStats: MemberStats[];
}

interface MemberExcelExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

const MemberExcelExportModal: React.FC<MemberExcelExportModalProps> = ({
  isOpen,
  onClose,
  isMobile = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportData, setExportData] = useState<ExportData | null>(null);
  const [activeTab, setActiveTab] = useState<'members' | 'dogs' | 'payments' | 'stats' | 'statistics' | 'events' | 'dogs-extended'>('members');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchExportData();
    }
  }, [isOpen]);

  const handleClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  const fetchExportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/members/excel-export');
      if (response.data.success) {
        setExportData(response.data.data);
      } else {
        setError('Fehler beim Laden der Daten');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = useMemo(() => {
    if (!exportData?.members) return [];
    const members = includeDeleted
      ? [...exportData.members]
      : exportData.members.filter(m => !m.geloescht);
    // Immer alphabetisch nach Vorname, dann Nachname sortieren
    return members.sort((a, b) => {
      const vornameCompare = (a.vorname || '').localeCompare(b.vorname || '', 'de');
      if (vornameCompare !== 0) return vornameCompare;
      return (a.name || '').localeCompare(b.name || '', 'de');
    });
  }, [exportData?.members, includeDeleted]);

  // Sortierte Zahlungsdaten nach Vorname
  const sortedPayments = useMemo(() => {
    if (!exportData?.paymentsByMember) return [];
    return [...exportData.paymentsByMember].sort((a, b) => {
      const vornameCompare = (a.vorname || '').localeCompare(b.vorname || '', 'de');
      if (vornameCompare !== 0) return vornameCompare;
      return (a.name || '').localeCompare(b.name || '', 'de');
    });
  }, [exportData?.paymentsByMember]);

  // Sortierte Hundedaten nach Vorname
  const sortedDogs = useMemo(() => {
    if (!exportData?.dogsByMember) return [];
    return [...exportData.dogsByMember].sort((a, b) => {
      const vornameCompare = (a.vorname || '').localeCompare(b.vorname || '', 'de');
      if (vornameCompare !== 0) return vornameCompare;
      return (a.name || '').localeCompare(b.name || '', 'de');
    });
  }, [exportData?.dogsByMember]);

  // Berechnete Statistiken
  const statistics = useMemo(() => {
    if (!filteredMembers.length) return null;

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    // Altersverteilung
    const ageGroups = { '0-18': 0, '19-30': 0, '31-50': 0, '51-65': 0, '65+': 0, 'Unbekannt': 0 };
    filteredMembers.forEach(m => {
      if (!m.geb_am) { ageGroups['Unbekannt']++; return; }
      const birthYear = new Date(m.geb_am).getFullYear();
      const age = currentYear - birthYear;
      if (age <= 18) ageGroups['0-18']++;
      else if (age <= 30) ageGroups['19-30']++;
      else if (age <= 50) ageGroups['31-50']++;
      else if (age <= 65) ageGroups['51-65']++;
      else ageGroups['65+']++;
    });

    // Geschlechterverteilung
    const genderStats = { M: 0, W: 0, Unbekannt: 0 };
    filteredMembers.forEach(m => {
      if (m.gender === false) genderStats.M++;
      else if (m.gender === true) genderStats.W++;
      else genderStats.Unbekannt++;
    });

    // PLZ-Verteilung (Top 10)
    const plzMap: Record<string, number> = {};
    filteredMembers.forEach(m => {
      const plz = m.plz || 'Unbekannt';
      plzMap[plz] = (plzMap[plz] || 0) + 1;
    });
    const plzStats = Object.entries(plzMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    // Eintrittsmonate
    const entryMonths: Record<string, number> = {};
    const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    monthNames.forEach(m => entryMonths[m] = 0);
    filteredMembers.forEach(m => {
      if (m.eintritt) {
        const month = new Date(m.eintritt).getMonth();
        entryMonths[monthNames[month]]++;
      }
    });

    // Geburtstage dieses Jahr (nächste 3 Monate)
    const upcomingBirthdays = filteredMembers
      .filter(m => m.geb_am)
      .map(m => {
        const bday = new Date(m.geb_am!);
        const thisYearBday = new Date(currentYear, bday.getMonth(), bday.getDate());
        if (thisYearBday < new Date()) {
          thisYearBday.setFullYear(currentYear + 1);
        }
        return { ...m, nextBirthday: thisYearBday, age: currentYear - bday.getFullYear() };
      })
      .sort((a, b) => a.nextBirthday.getTime() - b.nextBirthday.getTime())
      .slice(0, 20);

    // Jubiläen (5, 10, 15, 20, 25, 30+ Jahre Mitgliedschaft)
    const jubilees = filteredMembers
      .filter(m => m.eintritt)
      .map(m => {
        const entryYear = new Date(m.eintritt!).getFullYear();
        const years = currentYear - entryYear;
        return { ...m, membershipYears: years };
      })
      .filter(m => [5, 10, 15, 20, 25, 30, 35, 40, 45, 50].includes(m.membershipYears))
      .sort((a, b) => b.membershipYears - a.membershipYears);

    // Ausdrücklich eingetragene, aktuell gültige Ehrenmitgliedschaften.
    const honors = filteredMembers
      .filter(m => honoraryOnDate(m))
      .map(m => {
        const entryYear = m.eintritt ? new Date(m.eintritt).getFullYear() : currentYear;
        const years = currentYear - entryYear;
        return { ...m, membershipYears: years };
      })
      .sort((a, b) => b.membershipYears - a.membershipYears);

    return { ageGroups, genderStats, plzStats, entryMonths, upcomingBirthdays, jubilees, honors };
  }, [filteredMembers]);

  // Erweiterte Hunde-Statistiken
  const dogsExtended = useMemo(() => {
    if (!exportData?.dogsByMember) return null;

    const allDogs: Array<{
      memberName: string;
      memberVorname: string;
      dogName: string;
      versicherung: string;
      tollwutBis: string | null;
      isExpired: boolean;
      isExpiringSoon: boolean;
      hasInsurance: boolean;
    }> = [];

    sortedDogs.forEach(member => {
      member.dogs.forEach(dog => {
        const tollwutDate = dog.tollwut_gueltig_bis ? new Date(dog.tollwut_gueltig_bis) : null;
        const now = new Date();
        const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

        allDogs.push({
          memberName: member.name,
          memberVorname: member.vorname,
          dogName: dog.name,
          versicherung: dog.versicherung,
          tollwutBis: dog.tollwut_gueltig_bis,
          isExpired: tollwutDate ? tollwutDate < now : false,
          isExpiringSoon: tollwutDate ? (tollwutDate >= now && tollwutDate <= in30Days) : false,
          hasInsurance: dog.valid_insurance
        });
      });
    });

    const expiredTollwut = allDogs.filter(d => d.isExpired);
    const expiringSoonTollwut = allDogs.filter(d => d.isExpiringSoon);
    const noInsurance = allDogs.filter(d => !d.hasInsurance);

    return { allDogs, expiredTollwut, expiringSoonTollwut, noInsurance };
  }, [exportData?.dogsByMember]);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatBoolean = (val: number | boolean): string => val ? '1' : '0';

  const exportToExcel = async () => {
    if (!exportData) return;

    const workbook = new ExcelJS.Workbook();

    // Style definitions
    const FONT_FAMILY_MAIN = 'Arial';
    const FONT_FAMILY_HEADINGS = 'Arial Black';
    const ACCENT_COLOR_PRIMARY = '2E7D32';
    const HEADER_FILL = 'D3D3D3';
    const TEXT_COLOR_DARK = '333333';
    const BORDER_COLOR_DARK = '000000';
    const ALT_ROW_FILL = 'F8F9FA';
    const POSITIVE_VALUE = '008000';
    const NEGATIVE_VALUE = 'C00000';
    const WARNING_VALUE = 'FFA500';

    const titleStyle: Partial<ExcelJS.Style> = {
      font: { name: FONT_FAMILY_HEADINGS, size: 16, bold: true, color: { argb: ACCENT_COLOR_PRIMARY } },
      alignment: { horizontal: 'left', vertical: 'middle' },
    };

    const headerStyle: Partial<ExcelJS.Style> = {
      font: { name: FONT_FAMILY_MAIN, size: 10, bold: true, color: { argb: TEXT_COLOR_DARK } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } },
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      border: {
        top: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
        bottom: { style: 'medium', color: { argb: BORDER_COLOR_DARK } },
        left: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
        right: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
      },
    };

    const baseCellStyle: Partial<ExcelJS.Style> = {
      font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: TEXT_COLOR_DARK } },
      alignment: { vertical: 'middle', horizontal: 'left' },
      border: {
        top: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
        bottom: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
        left: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
        right: { style: 'thin', color: { argb: BORDER_COLOR_DARK } },
      },
    };

    // =============================================
    // TABLE 1: Member Overview
    // =============================================
    const membersSheet = workbook.addWorksheet('Mitgliederübersicht');

    membersSheet.getRow(1).getCell(1).value = 'HSV-Berlin-Adlershof e.V. - Mitgliederübersicht';
    membersSheet.getRow(1).getCell(1).style = titleStyle;
    membersSheet.getRow(2).getCell(1).value = `Stand: ${formatDate(new Date().toISOString())}`;

    const membersHeaders = ['MG-Nr.', 'Name', 'Vorname', 'Geb. am', 'Straße', 'M / W', 'PLZ', 'ORT', 'Tel.-Nummer', 'E-Mail', 'Eintritt', 'Datenschutz', 'Homepage', 'Facebook', 'Presse', 'Kündigung eingegangen', 'Mitglied bis einschließlich', 'Mitgliedschaftsstatus'];
    membersSheet.getRow(4).values = membersHeaders;
    membersHeaders.forEach((_, index) => {
      membersSheet.getRow(4).getCell(index + 1).style = headerStyle;
    });

    filteredMembers.forEach((member, rowIndex) => {
      const dataRow = membersSheet.getRow(rowIndex + 5);
      dataRow.values = [
        member.mitglied_nr || '-',
        member.name || '-',
        member.vorname || '-',
        formatDate(member.geb_am),
        member.strasse || '- - -',
        member.gender === false ? 'M' : member.gender === true ? 'W' : '-',
        member.plz || '-',
        member.ort || '-',
        member.tel_nummer || '-',
        member.email || '-',
        formatDate(member.eintritt),
        formatBoolean(member.datenschutzerklaerung),
        formatBoolean(member.homepage),
        formatBoolean(member.facebook),
        formatBoolean(member.presse),
        member.kuendigung_am ? new Date(`${datePart(member.kuendigung_am)}T12:00:00Z`) : null,
        membershipEnd(member) ? new Date(`${membershipEnd(member)}T12:00:00Z`) : null,
        membershipStatus(member)
      ];

      const rowFill: ExcelJS.Fill = rowIndex % 2 === 0
        ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
        : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };

      for (let i = 1; i <= membersHeaders.length; i++) {
        dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
      }
    });

    membersSheet.columns = [
      { width: 12 }, { width: 15 }, { width: 15 }, { width: 12 }, { width: 25 },
      { width: 8 }, { width: 8 }, { width: 15 }, { width: 15 }, { width: 25 },
      { width: 12 }, { width: 12 }, { width: 10 }, { width: 10 }, { width: 8 },
      { width: 23 }, { width: 25 }, { width: 23 }
    ];
    membersSheet.getColumn(16).numFmt = 'dd.mm.yyyy';
    membersSheet.getColumn(17).numFmt = 'dd.mm.yyyy';

    // =============================================
    // TABLE 2: Liability & Rabies
    // =============================================
    const dogsSheet = workbook.addWorksheet('Haftpflicht & Tollwut');

    dogsSheet.getRow(1).getCell(1).value = 'HSV-Berlin-Adlershof e.V. - Haftpflicht & Tollwut-Status';
    dogsSheet.getRow(1).getCell(1).style = titleStyle;
    dogsSheet.getRow(2).getCell(1).value = `Stand: ${formatDate(new Date().toISOString())}`;

    const dogsHeaders = ['Vorname', 'Name', 'Name Hund', 'Versicherung', 'Tollwut gültig bis', 'Status'];
    dogsSheet.getRow(4).values = dogsHeaders;
    dogsHeaders.forEach((_, index) => {
      dogsSheet.getRow(4).getCell(index + 1).style = headerStyle;
    });

    let dogRowIndex = 0;
    sortedDogs.forEach((member) => {
      if (member.dogs.length === 0) {
        const dataRow = dogsSheet.getRow(dogRowIndex + 5);
        dataRow.values = [member.vorname || '-', member.name || '-', '-', '-', '-', '-'];
        const rowFill: ExcelJS.Fill = dogRowIndex % 2 === 0
          ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
          : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };
        for (let i = 1; i <= 6; i++) {
          dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
        }
        dogRowIndex++;
      } else {
        member.dogs.forEach((dog) => {
          const dataRow = dogsSheet.getRow(dogRowIndex + 5);
          const tollwutDate = dog.tollwut_gueltig_bis ? new Date(dog.tollwut_gueltig_bis) : null;
          const isExpired = tollwutDate && tollwutDate < new Date();
          const isExpiringSoon = tollwutDate && !isExpired && tollwutDate < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

          let status = 'OK';
          let statusColor = POSITIVE_VALUE;
          if (!dog.valid_insurance || !dog.valid_tollwut || isExpired) {
            status = 'Problem';
            statusColor = NEGATIVE_VALUE;
          } else if (isExpiringSoon) {
            status = 'Bald ablaufend';
            statusColor = WARNING_VALUE;
          }

          dataRow.values = [
            member.vorname || '-',
            member.name || '-',
            dog.name || '-',
            dog.versicherung || '-',
            formatDate(dog.tollwut_gueltig_bis),
            status
          ];

          const rowFill: ExcelJS.Fill = dogRowIndex % 2 === 0
            ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
            : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };

          for (let i = 1; i <= 5; i++) {
            dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
          }
          dataRow.getCell(6).style = {
            ...baseCellStyle,
            fill: rowFill,
            font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: statusColor }, bold: true }
          };

          dogRowIndex++;
        });
      }
    });

    dogsSheet.columns = [
      { width: 15 }, { width: 15 }, { width: 20 }, { width: 20 }, { width: 18 }, { width: 15 }
    ];

    // =============================================
    // TABLES 3+: Member contributions per year
    // =============================================
    const years = exportData.availableYears || [];
    const contributionsMap: Record<number, Contribution> = {};
    exportData.contributions?.forEach(c => {
      contributionsMap[c.year] = c;
    });

    const NEW_ENTRY_FILL = 'E3F2FD'; // Light blue for new entries
    const PREV_YEAR_DEBT_FILL = 'FFF3E0'; // Light orange for previous year debt

    years.forEach((year, yearIndex) => {
      const yearSheet = workbook.addWorksheet(`Beiträge ${year}`);
      const prevYear = years[yearIndex + 1]; // Previous year in the list

      yearSheet.getRow(1).getCell(1).value = `HSV-Berlin-Adlershof e.V. - Mitgliedsbeiträge ${year}`;
      yearSheet.getRow(1).getCell(1).style = titleStyle;

      const contrib = contributionsMap[year];
      if (contrib) {
        yearSheet.getRow(2).getCell(1).value = `Beitragssätze: Erwachsen ${contrib.adults}€, Partner ${contrib.partner}€, Jugendlich ${contrib.youth}€`;
      }
      yearSheet.getRow(3).getCell(1).value = '★ NEU = In diesem Jahr eingetreten | ⚠ = Vorjahr-Rückstand';
      yearSheet.getRow(3).getCell(1).style = {
        font: { name: FONT_FAMILY_MAIN, size: 9, italic: true, color: { argb: '666666' } }
      };

      const paymentHeaders = ['MG-Nr.', 'Vorname', 'Name', 'Eintritt', 'Austritt', 'Betrag', 'Status', 'Hinweis'];
      yearSheet.getRow(5).values = paymentHeaders;
      paymentHeaders.forEach((_, index) => {
        yearSheet.getRow(5).getCell(index + 1).style = headerStyle;
      });

      let paymentRowIndex = 0;
      let totalPaid = 0;
      let totalOpen = 0;

      sortedPayments.forEach((member) => {
        const eintritt = member.eintritt ? new Date(member.eintritt) : null;
        const geloescht = membershipEnd(member) ? new Date(membershipEnd(member)) : null;
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);

        const wasActiveInYear = eintritt && eintritt <= yearEnd &&
          (!geloescht || geloescht >= yearStart);

        if (!wasActiveInYear && !member.payments[year]?.betrag) return;

        const payment = member.payments[year];
        const betrag = payment?.betrag || 0;
        totalPaid += betrag;
        const isNewEntry = eintritt && eintritt.getFullYear() === year;

        // Check for previous year debt
        let hasPrevYearDebt = false;
        if (prevYear && membershipInYear(member, prevYear)) {
          const prevPayment = member.payments[prevYear];
          hasPrevYearDebt = !honoraryInYear(member, prevYear) && (!prevPayment || prevPayment.betrag === 0);
        }

        let status = 'Offen';
        let statusColor = NEGATIVE_VALUE;
        if (betrag > 0) {
          status = 'Bezahlt';
          statusColor = POSITIVE_VALUE;
        } else if (honoraryInYear(member, year)) {
          status = 'Beitragsfrei (Ehrenmitglied)';
          statusColor = POSITIVE_VALUE;
        } else if (!wasActiveInYear) {
          status = 'Nicht Mitglied';
          statusColor = 'FF6600';
        } else {
          totalOpen++;
        }

        // Build hint text
        let hint = '';
        if (member.austritt_am) hint = `Mitglied bis ${formatDate(member.austritt_am)}`;
        if (isNewEntry) hint += hint ? ' | ★ NEU' : '★ NEU';
        if (hasPrevYearDebt) hint += hint ? ' | ⚠' : '⚠ Rückstand';

        const dataRow = yearSheet.getRow(paymentRowIndex + 6);
        dataRow.values = [
          member.mitglied_nr || '-',
          member.vorname || '-',
          member.name || '-',
          formatDate(member.eintritt),
          membershipEnd(member) ? formatDate(membershipEnd(member)) : '-',
          betrag,
          status,
          hint || null
        ];

        // Use light blue for new entries, light orange for prev year debt
        let rowFill: ExcelJS.Fill;
        if (hasPrevYearDebt) {
          rowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: PREV_YEAR_DEBT_FILL } };
        } else if (isNewEntry) {
          rowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NEW_ENTRY_FILL } };
        } else {
          rowFill = paymentRowIndex % 2 === 0
            ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
            : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };
        }

        for (let i = 1; i <= 6; i++) {
          dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
        }
        dataRow.getCell(7).style = {
          ...baseCellStyle,
          fill: rowFill,
          font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: statusColor }, bold: true }
        };
        dataRow.getCell(8).style = {
          ...baseCellStyle,
          fill: rowFill,
          font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: hasPrevYearDebt ? 'FF6600' : '1976D2' }, bold: true },
          alignment: { horizontal: 'center', vertical: 'middle' }
        };

        dataRow.getCell(6).numFmt = '#,##0.00" €"';
        paymentRowIndex++;
      });

      // Add summary row
      const summaryRow = yearSheet.getRow(paymentRowIndex + 7);
      summaryRow.values = ['', '', '', '', 'SUMME:', totalPaid, `${totalOpen} offen`, ''];
      const summaryFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0E0E0' } };
      for (let i = 1; i <= 8; i++) {
        summaryRow.getCell(i).style = {
          ...baseCellStyle,
          fill: summaryFill,
          font: { name: FONT_FAMILY_MAIN, size: 11, bold: true, color: { argb: TEXT_COLOR_DARK } }
        };
      }
      summaryRow.getCell(6).style = {
        ...baseCellStyle,
        fill: summaryFill,
        font: { name: FONT_FAMILY_MAIN, size: 11, bold: true, color: { argb: POSITIVE_VALUE } },
        alignment: { horizontal: 'right', vertical: 'middle' }
      };
      summaryRow.getCell(7).style = {
        ...baseCellStyle,
        fill: summaryFill,
        font: { name: FONT_FAMILY_MAIN, size: 11, bold: true, color: { argb: totalOpen > 0 ? NEGATIVE_VALUE : POSITIVE_VALUE } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      summaryRow.getCell(6).numFmt = '#,##0.00" €"';
      yearSheet.columns = [
        { width: 12 }, { width: 15 }, { width: 15 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 27 }, { width: 38 }
      ];
    });

    // =============================================
    // TABLE: Member Development Statistics
    // =============================================
    if (exportData.memberStats && exportData.memberStats.length > 0) {
      const statsSheet = workbook.addWorksheet('Mitgliederentwicklung');

      statsSheet.getRow(1).getCell(1).value = 'HSV-Berlin-Adlershof e.V. - Mitgliederentwicklung';
      statsSheet.getRow(1).getCell(1).style = titleStyle;
      statsSheet.getRow(2).getCell(1).value = `Stand: ${formatDate(new Date().toISOString())}`;

      const statsHeaders = ['Jahr', 'Eintritte', 'Austritte', 'Veränderung', 'Mitgliederbestand'];
      statsSheet.getRow(4).values = statsHeaders;
      statsHeaders.forEach((_, index) => {
        statsSheet.getRow(4).getCell(index + 1).style = headerStyle;
      });

      exportData.memberStats.forEach((stat, rowIndex) => {
        const dataRow = statsSheet.getRow(rowIndex + 5);
        const change = stat.eintritte - stat.austritte;

        dataRow.values = [
          stat.year,
          `+${stat.eintritte}`,
          stat.austritte > 0 ? `-${stat.austritte}` : '0',
          change >= 0 ? `+${change}` : `${change}`,
          stat.totalMembers
        ];

        const rowFill: ExcelJS.Fill = rowIndex % 2 === 0
          ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
          : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };

        dataRow.getCell(1).style = { ...baseCellStyle, fill: rowFill, font: { name: FONT_FAMILY_MAIN, size: 10, bold: true } };
        dataRow.getCell(2).style = {
          ...baseCellStyle,
          fill: rowFill,
          font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: POSITIVE_VALUE }, bold: true },
          alignment: { horizontal: 'center', vertical: 'middle' }
        };
        dataRow.getCell(3).style = {
          ...baseCellStyle,
          fill: rowFill,
          font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: stat.austritte > 0 ? NEGATIVE_VALUE : TEXT_COLOR_DARK } },
          alignment: { horizontal: 'center', vertical: 'middle' }
        };
        dataRow.getCell(4).style = {
          ...baseCellStyle,
          fill: rowFill,
          font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: change >= 0 ? POSITIVE_VALUE : NEGATIVE_VALUE }, bold: true },
          alignment: { horizontal: 'center', vertical: 'middle' }
        };
        dataRow.getCell(5).style = {
          ...baseCellStyle,
          fill: rowFill,
          font: { name: FONT_FAMILY_MAIN, size: 12, color: { argb: '1976D2' }, bold: true },
          alignment: { horizontal: 'center', vertical: 'middle' }
        };
      });

      statsSheet.columns = [
        { width: 10 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 18 }
      ];
    }

    // =============================================
    // TABLE: Statistik (Alters-, Geschlechter-, PLZ-Verteilung)
    // =============================================
    if (statistics) {
      const statistikSheet = workbook.addWorksheet('Statistik');

      statistikSheet.getRow(1).getCell(1).value = 'HSV-Berlin-Adlershof e.V. - Mitgliederstatistik';
      statistikSheet.getRow(1).getCell(1).style = titleStyle;
      statistikSheet.getRow(2).getCell(1).value = `Stand: ${formatDate(new Date().toISOString())} | Gesamt: ${filteredMembers.length} Mitglieder`;

      // Altersverteilung
      statistikSheet.getRow(4).getCell(1).value = 'Altersverteilung';
      statistikSheet.getRow(4).getCell(1).style = { font: { name: FONT_FAMILY_MAIN, size: 12, bold: true } };

      const ageHeaders = ['Altersgruppe', 'Anzahl', 'Anteil'];
      statistikSheet.getRow(5).values = ageHeaders;
      ageHeaders.forEach((_, index) => {
        statistikSheet.getRow(5).getCell(index + 1).style = headerStyle;
      });

      let ageRowIndex = 0;
      const totalMembers = filteredMembers.length;
      Object.entries(statistics.ageGroups).forEach(([group, count]) => {
        const dataRow = statistikSheet.getRow(ageRowIndex + 6);
        const percentage = totalMembers > 0 ? ((count / totalMembers) * 100).toFixed(1) : '0';
        dataRow.values = [group, count, `${percentage}%`];
        const rowFill: ExcelJS.Fill = ageRowIndex % 2 === 0
          ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
          : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };
        for (let i = 1; i <= 3; i++) {
          dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
        }
        ageRowIndex++;
      });

      // Geschlechterverteilung
      const genderStartRow = ageRowIndex + 8;
      statistikSheet.getRow(genderStartRow).getCell(1).value = 'Geschlechterverteilung';
      statistikSheet.getRow(genderStartRow).getCell(1).style = { font: { name: FONT_FAMILY_MAIN, size: 12, bold: true } };

      const genderHeaders = ['Geschlecht', 'Anzahl', 'Anteil'];
      statistikSheet.getRow(genderStartRow + 1).values = genderHeaders;
      genderHeaders.forEach((_, index) => {
        statistikSheet.getRow(genderStartRow + 1).getCell(index + 1).style = headerStyle;
      });

      let genderRowIndex = 0;
      Object.entries(statistics.genderStats).forEach(([gender, count]) => {
        const dataRow = statistikSheet.getRow(genderStartRow + 2 + genderRowIndex);
        const genderLabel = gender === 'M' ? 'Männlich' : gender === 'W' ? 'Weiblich' : 'Unbekannt';
        const percentage = totalMembers > 0 ? ((count / totalMembers) * 100).toFixed(1) : '0';
        dataRow.values = [genderLabel, count, `${percentage}%`];
        const rowFill: ExcelJS.Fill = genderRowIndex % 2 === 0
          ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
          : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };
        for (let i = 1; i <= 3; i++) {
          dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
        }
        genderRowIndex++;
      });

      // PLZ-Verteilung
      const plzStartRow = genderStartRow + genderRowIndex + 4;
      statistikSheet.getRow(plzStartRow).getCell(1).value = 'PLZ-Verteilung (Top 10)';
      statistikSheet.getRow(plzStartRow).getCell(1).style = { font: { name: FONT_FAMILY_MAIN, size: 12, bold: true } };

      const plzHeaders = ['PLZ', 'Anzahl', 'Anteil'];
      statistikSheet.getRow(plzStartRow + 1).values = plzHeaders;
      plzHeaders.forEach((_, index) => {
        statistikSheet.getRow(plzStartRow + 1).getCell(index + 1).style = headerStyle;
      });

      let plzRowIndex = 0;
      statistics.plzStats.forEach(([plz, count]) => {
        const dataRow = statistikSheet.getRow(plzStartRow + 2 + plzRowIndex);
        const percentage = totalMembers > 0 ? ((count / totalMembers) * 100).toFixed(1) : '0';
        dataRow.values = [plz, count, `${percentage}%`];
        const rowFill: ExcelJS.Fill = plzRowIndex % 2 === 0
          ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
          : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };
        for (let i = 1; i <= 3; i++) {
          dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
        }
        plzRowIndex++;
      });

      statistikSheet.columns = [
        { width: 15 }, { width: 12 }, { width: 12 }
      ];
    }

    // =============================================
    // TABLE: Termine (Geburtstage, Jubiläen, Ehrungen)
    // =============================================
    if (statistics) {
      const termineSheet = workbook.addWorksheet('Termine');

      termineSheet.getRow(1).getCell(1).value = 'HSV-Berlin-Adlershof e.V. - Termine & Ehrungen';
      termineSheet.getRow(1).getCell(1).style = titleStyle;
      termineSheet.getRow(2).getCell(1).value = `Stand: ${formatDate(new Date().toISOString())}`;

      // Geburtstage
      termineSheet.getRow(4).getCell(1).value = '🎂 Nächste Geburtstage';
      termineSheet.getRow(4).getCell(1).style = { font: { name: FONT_FAMILY_MAIN, size: 12, bold: true } };

      const bdayHeaders = ['Name', 'Vorname', 'Datum', 'Wird'];
      termineSheet.getRow(5).values = bdayHeaders;
      bdayHeaders.forEach((_, index) => {
        termineSheet.getRow(5).getCell(index + 1).style = headerStyle;
      });

      statistics.upcomingBirthdays.slice(0, 20).forEach((m, index) => {
        const dataRow = termineSheet.getRow(index + 6);
        dataRow.values = [
          m.name,
          m.vorname,
          m.nextBirthday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          `${m.age + 1} Jahre`
        ];
        const rowFill: ExcelJS.Fill = index % 2 === 0
          ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
          : { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_FILL } };
        for (let i = 1; i <= 4; i++) {
          dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
        }
      });

      // Jubiläen
      const jubStartRow = Math.max(26, statistics.upcomingBirthdays.length + 8);
      termineSheet.getRow(jubStartRow).getCell(1).value = '🏆 Jubiläen dieses Jahr';
      termineSheet.getRow(jubStartRow).getCell(1).style = { font: { name: FONT_FAMILY_MAIN, size: 12, bold: true } };

      const jubHeaders = ['Name', 'Vorname', 'Eintritt', 'Jahre'];
      termineSheet.getRow(jubStartRow + 1).values = jubHeaders;
      jubHeaders.forEach((_, index) => {
        termineSheet.getRow(jubStartRow + 1).getCell(index + 1).style = headerStyle;
      });

      statistics.jubilees.forEach((m, index) => {
        const dataRow = termineSheet.getRow(jubStartRow + 2 + index);
        dataRow.values = [
          m.name,
          m.vorname,
          formatDate(m.eintritt),
          `${m.membershipYears} Jahre`
        ];
        const rowFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: m.membershipYears >= 25 ? 'FFF9C4' : 'FFFFFF' } };
        for (let i = 1; i <= 4; i++) {
          dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
        }
        dataRow.getCell(4).style = {
          ...baseCellStyle,
          fill: rowFill,
          font: { name: FONT_FAMILY_MAIN, size: 10, bold: true, color: { argb: m.membershipYears >= 25 ? 'F57F17' : '1976D2' } }
        };
      });

      // Ehrenmitglieder
      const honorsStartRow = jubStartRow + statistics.jubilees.length + 4;
      termineSheet.getRow(honorsStartRow).getCell(1).value = '🎖️ Ehrenmitglieder';
      termineSheet.getRow(honorsStartRow).getCell(1).style = { font: { name: FONT_FAMILY_MAIN, size: 12, bold: true } };

      const honorsHeaders = ['Name', 'Vorname', 'Eintritt', 'Mitgliedsjahre'];
      termineSheet.getRow(honorsStartRow + 1).values = honorsHeaders;
      honorsHeaders.forEach((_, index) => {
        termineSheet.getRow(honorsStartRow + 1).getCell(index + 1).style = headerStyle;
      });

      statistics.honors.forEach((m, index) => {
        const dataRow = termineSheet.getRow(honorsStartRow + 2 + index);
        dataRow.values = [
          m.name,
          m.vorname,
          formatDate(m.eintritt),
          m.membershipYears >= 30 ? `🌟 ${m.membershipYears}` : m.membershipYears
        ];
        const rowFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: m.membershipYears >= 30 ? 'FFF3E0' : 'FFFDE7' } };
        for (let i = 1; i <= 4; i++) {
          dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
        }
        dataRow.getCell(4).style = {
          ...baseCellStyle,
          fill: rowFill,
          font: { name: FONT_FAMILY_MAIN, size: 10, bold: true, color: { argb: 'E65100' } }
        };
      });

      termineSheet.columns = [
        { width: 18 }, { width: 15 }, { width: 14 }, { width: 14 }
      ];
    }

    // =============================================
    // TABLE: Hunde Erweitert (Vollständige Hundeliste)
    // =============================================
    if (dogsExtended) {
      const hundeSheet = workbook.addWorksheet('Hunde Erweitert');

      hundeSheet.getRow(1).getCell(1).value = 'HSV-Berlin-Adlershof e.V. - Hundeliste Erweitert';
      hundeSheet.getRow(1).getCell(1).style = titleStyle;
      hundeSheet.getRow(2).getCell(1).value = `Stand: ${formatDate(new Date().toISOString())} | Gesamt: ${dogsExtended.allDogs.length} Hunde`;
      hundeSheet.getRow(3).getCell(1).value = `⚠️ Abgelaufen: ${dogsExtended.expiredTollwut.length} | ⏰ Bald fällig: ${dogsExtended.expiringSoonTollwut.length} | 🔔 Ohne Versicherung: ${dogsExtended.noInsurance.length}`;
      hundeSheet.getRow(3).getCell(1).style = { font: { name: FONT_FAMILY_MAIN, size: 10, color: { argb: '666666' } } };

      const hundeHeaders = ['Besitzer', 'Hundename', 'Versicherung', 'Tollwut gültig bis', 'Status'];
      hundeSheet.getRow(5).values = hundeHeaders;
      hundeHeaders.forEach((_, index) => {
        hundeSheet.getRow(5).getCell(index + 1).style = headerStyle;
      });

      dogsExtended.allDogs.forEach((dog, index) => {
        const dataRow = hundeSheet.getRow(index + 6);
        let status = 'OK';
        let statusColor = POSITIVE_VALUE;
        let rowFillColor = index % 2 === 0 ? 'FFFFFF' : ALT_ROW_FILL;

        if (dog.isExpired) {
          status = '⚠️ Abgelaufen';
          statusColor = NEGATIVE_VALUE;
          rowFillColor = 'FFEBEE';
        } else if (dog.isExpiringSoon) {
          status = '⏰ Bald fällig';
          statusColor = WARNING_VALUE;
          rowFillColor = 'FFF3E0';
        } else if (!dog.hasInsurance) {
          status = '🔔 Keine Vers.';
          statusColor = 'DAA520';
          rowFillColor = 'FFFDE7';
        }

        dataRow.values = [
          `${dog.memberVorname} ${dog.memberName}`,
          dog.dogName,
          dog.versicherung || '-',
          formatDate(dog.tollwutBis),
          status
        ];

        const rowFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFillColor } };
        for (let i = 1; i <= 4; i++) {
          dataRow.getCell(i).style = { ...baseCellStyle, fill: rowFill };
        }
        dataRow.getCell(5).style = {
          ...baseCellStyle,
          fill: rowFill,
          font: { name: FONT_FAMILY_MAIN, size: 10, bold: true, color: { argb: statusColor } }
        };
      });

      hundeSheet.columns = [
        { width: 25 }, { width: 18 }, { width: 20 }, { width: 18 }, { width: 15 }
      ];
    }

    // Download Excel file
    const currentDate = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '-');
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Mitgliederliste_HSV_Berlin-Adlershof_${currentDate}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
    onClose();
  };

  return (
    <ModalShell
      open={isOpen}
      onClose={handleClose}
      variant="form"
      size="lg"
      icon={UserGroupIcon}
      title="Mitgliederliste Excel-Export"
      closeOnBackdrop={false}
      footer={
        <ModalActions
          secondary={{ label: 'Abbrechen', onClick: () => onClose() }}
          primary={{
            label: 'Excel exportieren',
            onClick: exportToExcel,
            disabled: loading || !exportData,
            icon: <ArrowDownTrayIcon className="h-4 w-4" />
          }}
        />
      }
    >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-success-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-300">Lade Daten...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="h-12 w-12 text-error-500 mx-auto mb-4" />
              <p className="text-error-500 mb-4">{error}</p>
              <button
                onClick={fetchExportData}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700"
              >
                Erneut versuchen
              </button>
            </div>
          ) : exportData ? (
            <>
              {/* Options */}
              <div className="mb-4 flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeDeleted}
                    onChange={(e) => setIncludeDeleted(e.target.checked)}
                    className="h-4 w-4 text-success-600 focus:ring-success-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  Archivierte in der Mitgliederübersicht einschließen
                </label>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('members')}
                  className={`px-4 py-2 -mb-px font-medium text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    activeTab === 'members'
                      ? 'border-b-2 border-success-600 text-success-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-success-600'
                  }`}
                >
                  <UserGroupIcon className="w-4 h-4" /> Mitglieder ({filteredMembers.length})
                </button>
                <button
                  onClick={() => setActiveTab('dogs')}
                  className={`px-4 py-2 -mb-px font-medium text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    activeTab === 'dogs'
                      ? 'border-b-2 border-success-600 text-success-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-success-600'
                  }`}
                >
                  <DogIcon className="w-4 h-4" /> Haftpflicht & Tollwut
                </button>
                <button
                  onClick={() => setActiveTab('payments')}
                  className={`px-4 py-2 -mb-px font-medium text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    activeTab === 'payments'
                      ? 'border-b-2 border-success-600 text-success-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-success-600'
                  }`}
                >
                  <ArrowDownTrayIcon className="w-4 h-4" /> Beiträge ({exportData.availableYears?.length || 0} Jahre)
                </button>
                <button
                  onClick={() => setActiveTab('stats')}
                  className={`px-4 py-2 -mb-px font-medium text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    activeTab === 'stats'
                      ? 'border-b-2 border-success-600 text-success-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-success-600'
                  }`}
                >
                  <ChartBarIcon className="w-4 h-4" /> Entwicklung
                </button>
                <button
                  onClick={() => setActiveTab('statistics')}
                  className={`px-4 py-2 -mb-px font-medium text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    activeTab === 'statistics'
                      ? 'border-b-2 border-success-600 text-success-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-success-600'
                  }`}
                >
                  <ChartPieIcon className="w-4 h-4" /> Statistik
                </button>
                <button
                  onClick={() => setActiveTab('events')}
                  className={`px-4 py-2 -mb-px font-medium text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    activeTab === 'events'
                      ? 'border-b-2 border-success-600 text-success-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-success-600'
                  }`}
                >
                  <CalendarDaysIcon className="w-4 h-4" /> Termine
                </button>
                <button
                  onClick={() => setActiveTab('dogs-extended')}
                  className={`px-4 py-2 -mb-px font-medium text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    activeTab === 'dogs-extended'
                      ? 'border-b-2 border-success-600 text-success-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-success-600'
                  }`}
                >
                  <ShieldExclamationIcon className="w-4 h-4" /> Hunde+
                </button>
              </div>

              {/* Preview Tables */}
              <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
                {activeTab === 'members' && (
                  <>
                    <div className="mb-2 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-3">
                      <span><strong>DS</strong> = Datenschutz</span>
                      <span><strong>HP</strong> = Homepage</span>
                      <span><strong>FB</strong> = Facebook</span>
                      <span><strong>PR</strong> = Presse</span>
                      {includeDeleted && (
                        <span className="ml-4 text-error-600 dark:text-error-400">
                          <span className="inline-block w-3 h-3 bg-error-100 dark:bg-error-900/30 border border-error-300 dark:border-error-700 mr-1"></span>
                          <span className="line-through">Gelöscht</span>
                        </span>
                      )}
                    </div>
                    <table className="w-full border-collapse text-sm min-w-[1200px]">
                    <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 z-10">
                      <tr>
                        {['MG-Nr.', 'Name', 'Vorname', 'Geb. am', 'Straße', 'M/W', 'PLZ', 'Ort', 'Tel.', 'E-Mail', 'Eintritt', 'DS', 'HP', 'FB', 'PR', 'Kündigung eingegangen', 'Mitglied bis einschließlich', 'Status'].map(header => (
                          <th key={header} className="border border-gray-200 dark:border-gray-600 p-2 text-left font-semibold text-gray-900 dark:text-white text-xs whitespace-nowrap">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.map((member, index) => {
                        const isDeleted = member.geloescht === 1;
                        return (
                          <tr
                            key={member.mg_nr}
                            className={`${isDeleted ? 'bg-error-50 dark:bg-error-900/20' : index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}`}
                          >
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs whitespace-nowrap ${isDeleted ? 'text-error-600 dark:text-error-400 line-through' : 'text-gray-900 dark:text-white'}`}>{member.mitglied_nr || '-'}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs ${isDeleted ? 'text-error-600 dark:text-error-400 line-through' : 'text-gray-900 dark:text-white'}`}>{member.name}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs ${isDeleted ? 'text-error-600 dark:text-error-400 line-through' : 'text-gray-900 dark:text-white'}`}>{member.vorname}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs whitespace-nowrap ${isDeleted ? 'text-error-600 dark:text-error-400' : 'text-gray-900 dark:text-white'}`}>{formatDate(member.geb_am)}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs ${isDeleted ? 'text-error-600 dark:text-error-400' : 'text-gray-900 dark:text-white'}`}>{member.strasse || '-'}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs text-center ${isDeleted ? 'text-error-600 dark:text-error-400' : 'text-gray-900 dark:text-white'}`}>{member.gender === false ? 'M' : member.gender === true ? 'W' : '-'}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs ${isDeleted ? 'text-error-600 dark:text-error-400' : 'text-gray-900 dark:text-white'}`}>{member.plz || '-'}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs ${isDeleted ? 'text-error-600 dark:text-error-400' : 'text-gray-900 dark:text-white'}`}>{member.ort || '-'}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs ${isDeleted ? 'text-error-600 dark:text-error-400' : 'text-gray-900 dark:text-white'}`}>{member.tel_nummer || '-'}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs ${isDeleted ? 'text-error-600 dark:text-error-400' : 'text-gray-900 dark:text-white'}`}>{member.email || '-'}</td>
                            <td className={`border border-gray-200 dark:border-gray-600 p-2 text-xs whitespace-nowrap ${isDeleted ? 'text-error-600 dark:text-error-400' : 'text-gray-900 dark:text-white'}`}>{formatDate(member.eintritt)}</td>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-center text-xs">{member.datenschutzerklaerung ? <CheckCircleIcon className="w-4 h-4 text-success-500 inline" /> : <XCircleIcon className="w-4 h-4 text-error-400 inline" />}</td>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-center text-xs">{member.homepage ? <CheckCircleIcon className="w-4 h-4 text-success-500 inline" /> : <XCircleIcon className="w-4 h-4 text-error-400 inline" />}</td>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-center text-xs">{member.facebook ? <CheckCircleIcon className="w-4 h-4 text-success-500 inline" /> : <XCircleIcon className="w-4 h-4 text-error-400 inline" />}</td>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-center text-xs">{member.presse ? <CheckCircleIcon className="w-4 h-4 text-success-500 inline" /> : <XCircleIcon className="w-4 h-4 text-error-400 inline" />}</td>
                            <td className="border border-gray-200 p-2 text-xs whitespace-nowrap dark:border-gray-600">{formatDate(member.kuendigung_am || null)}</td>
                            <td className="border border-gray-200 p-2 text-xs whitespace-nowrap dark:border-gray-600">{formatDate(membershipEnd(member) || null)}</td>
                            <td className="border border-gray-200 p-2 text-xs dark:border-gray-600">{membershipStatus(member)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </>
                )}

                {activeTab === 'dogs' && (
                  <table className="w-full border-collapse text-sm">
                    <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700">
                      <tr>
                        {['Vorname', 'Name', 'Name Hund', 'Versicherung', 'Tollwut gültig bis', 'Status'].map(header => (
                          <th key={header} className="border border-gray-200 dark:border-gray-600 p-2 text-left font-semibold text-gray-900 dark:text-white">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDogs.flatMap((member, mIndex) =>
                        member.dogs.length === 0 ? (
                          <tr key={member.mg_nr} className={mIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{member.vorname}</td>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{member.name}</td>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-500">-</td>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-500">-</td>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-500">-</td>
                            <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-500">-</td>
                          </tr>
                        ) : (
                          member.dogs.map((dog, dIndex) => {
                            const tollwutDate = dog.tollwut_gueltig_bis ? new Date(dog.tollwut_gueltig_bis) : null;
                            const isExpired = tollwutDate && tollwutDate < new Date();
                            const allOk = dog.valid_insurance && dog.valid_tollwut && !isExpired;

                            return (
                              <tr key={`${member.mg_nr}-${dIndex}`} className={(mIndex + dIndex) % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}>
                                <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{member.vorname}</td>
                                <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{member.name}</td>
                                <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{dog.name}</td>
                                <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{dog.versicherung}</td>
                                <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{formatDate(dog.tollwut_gueltig_bis)}</td>
                                <td className="border border-gray-200 dark:border-gray-600 p-2">
                                  {allOk ? (
                                    <span className="flex items-center gap-1 text-success-600">
                                      <CheckCircleIcon className="w-4 h-4" /> OK
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-error-500">
                                      <XCircleIcon className="w-4 h-4" /> Problem
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )
                      )}
                    </tbody>
                  </table>
                )}

                {activeTab === 'payments' && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Der Export enthält für jedes Jahr ({exportData.availableYears?.join(', ')}) ein separates Tabellenblatt mit allen Mitgliedsbeiträgen.
                    </p>
                    <div className="flex flex-wrap gap-4 mb-4 text-xs">
                      <span className="inline-flex items-center gap-1 text-info-600 dark:text-info-400">
                        <SparklesIcon className="w-4 h-4" /> Neu eingetreten
                      </span>
                      <span className="inline-flex items-center gap-1 text-warning-600 dark:text-warning-400">
                        <ExclamationTriangleIcon className="w-4 h-4" /> Vorjahr-Rückstand
                      </span>
                    </div>
                    <table className="w-full border-collapse text-sm">
                      <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 z-10">
                        <tr>
                          <th className="border border-gray-200 dark:border-gray-600 p-2 text-left font-semibold text-gray-900 dark:text-white">MG-Nr.</th>
                          <th className="border border-gray-200 dark:border-gray-600 p-2 text-left font-semibold text-gray-900 dark:text-white">Vorname</th>
                          <th className="border border-gray-200 dark:border-gray-600 p-2 text-left font-semibold text-gray-900 dark:text-white">Name</th>
                          <th className="border border-gray-200 dark:border-gray-600 p-2 text-left font-semibold text-gray-900 dark:text-white">Eintritt</th>
                          {exportData.availableYears?.map(year => (
                            <th key={year} className="border border-gray-200 dark:border-gray-600 p-2 text-center font-semibold text-gray-900 dark:text-white">{year}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedPayments.map((member, index) => {
                          const eintrittYear = member.eintritt ? new Date(member.eintritt).getFullYear() : null;
                          return (
                            <tr key={member.mg_nr} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}>
                              <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{member.mitglied_nr || '-'}</td>
                              <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{member.vorname}</td>
                              <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{member.name}</td>
                              <td className="border border-gray-200 dark:border-gray-600 p-2 text-gray-900 dark:text-white">{formatDate(member.eintritt)}</td>
                              {exportData.availableYears?.map((year, yearIndex) => {
                                const payment = member.payments[year];
                                const betrag = payment?.betrag || 0;
                                const isNewEntry = eintrittYear === year;
                                // Check if previous year has open payment (only if member was active)
                                const prevYear = exportData.availableYears?.[yearIndex + 1];
                                const prevPayment = prevYear ? member.payments[prevYear] : null;
                                const hasPrevYearDebt = prevYear && membershipInYear(member, prevYear) && (!prevPayment || prevPayment.betrag === 0) && !honoraryInYear(member, prevYear);

                                return (
                                  <td key={year} className={`border border-gray-200 dark:border-gray-600 p-2 text-center font-medium ${!membershipInYear(member, year) && !betrag ? 'text-gray-500' : betrag > 0 || honoraryInYear(member, year) ? 'text-success-600' : 'text-error-500'} ${isNewEntry ? 'bg-info-50 dark:bg-info-900/30' : ''} ${hasPrevYearDebt ? 'bg-warning-50 dark:bg-warning-900/30' : ''}`}>
                                    {isNewEntry && <SparklesIcon className="w-3 h-3 inline-block mr-1 text-info-500" />}
                                    {hasPrevYearDebt && <ExclamationTriangleIcon className="w-3 h-3 inline-block mr-1 text-warning-500" />}
                                    {betrag !== 0 ? `${betrag.toFixed(0)}€` : !membershipInYear(member, year) ? 'Nicht Mitglied' : honoraryInYear(member, year) ? 'Beitragsfrei' : 'Offen'}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-200 dark:bg-gray-600 font-bold">
                        <tr>
                          <td colSpan={4} className="border border-gray-300 dark:border-gray-500 p-2 text-right text-gray-900 dark:text-white">
                            Summe:
                          </td>
                          {exportData.availableYears?.map(year => {
                            let totalPaid = 0;
                            let totalOpen = 0;
                            sortedPayments.forEach(member => {
                              const eintritt = member.eintritt ? new Date(member.eintritt) : null;
                              const geloescht = membershipEnd(member) ? new Date(membershipEnd(member)) : null;
                              const yearStart = new Date(year, 0, 1);
                              const yearEnd = new Date(year, 11, 31);
                              const wasActiveInYear = eintritt && eintritt <= yearEnd && (!geloescht || geloescht >= yearStart);

                              const payment = member.payments[year];
                              totalPaid += payment?.betrag || 0;
                              if (wasActiveInYear && !(payment && payment.betrag > 0) && !honoraryInYear(member, year)) totalOpen++;
                            });
                            return (
                              <td key={year} className="border border-gray-300 dark:border-gray-500 p-2 text-center text-xs">
                                <div className="text-success-700 dark:text-success-400">{totalPaid.toFixed(0)}€</div>
                                <div className="text-error-600 dark:text-error-400">{totalOpen} offen</div>
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {activeTab === 'stats' && exportData.memberStats && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Mitgliederentwicklung über die Jahre - Eintritte, Austritte und Gesamtbestand.
                    </p>

                    {/* Professional Chart with recharts */}
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Entwicklung des Mitgliederbestands</h4>
                      <div style={{ height: '300px', width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            data={exportData.memberStats.map(stat => ({
                              jahr: stat.year.toString(),
                              Eintritte: stat.eintritte,
                              Austritte: stat.austritte,
                              Bestand: stat.totalMembers
                            }))}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis
                              dataKey="jahr"
                              tick={{ fontSize: 12, fill: '#666' }}
                              axisLine={{ stroke: '#ccc' }}
                            />
                            <YAxis
                              yAxisId="left"
                              tick={{ fontSize: 12, fill: '#666' }}
                              axisLine={{ stroke: '#ccc' }}
                              label={{ value: 'Anzahl', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#666' } }}
                            />
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              tick={{ fontSize: 12, fill: '#666' }}
                              axisLine={{ stroke: '#ccc' }}
                              label={{ value: 'Bestand', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: '#666' } }}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                              }}
                              formatter={(value: number, name: string) => [value, name]}
                            />
                            <Legend
                              wrapperStyle={{ paddingTop: '10px' }}
                              iconType="rect"
                            />
                            <Bar
                              yAxisId="left"
                              dataKey="Eintritte"
                              fill="#22c55e"
                              radius={[4, 4, 0, 0]}
                              name="Eintritte"
                            />
                            <Bar
                              yAxisId="left"
                              dataKey="Austritte"
                              fill="#ef4444"
                              radius={[4, 4, 0, 0]}
                              name="Austritte"
                            />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="Bestand"
                              stroke="#3b82f6"
                              strokeWidth={3}
                              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }}
                              activeDot={{ r: 8, fill: '#3b82f6' }}
                              name="Mitgliederbestand"
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Stats Table */}
                    <table className="w-full border-collapse text-sm">
                      <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th className="border border-gray-200 dark:border-gray-600 p-2 text-left font-semibold text-gray-900 dark:text-white">Jahr</th>
                          <th className="border border-gray-200 dark:border-gray-600 p-2 text-center font-semibold text-gray-900 dark:text-white">Eintritte</th>
                          <th className="border border-gray-200 dark:border-gray-600 p-2 text-center font-semibold text-gray-900 dark:text-white">Austritte</th>
                          <th className="border border-gray-200 dark:border-gray-600 p-2 text-center font-semibold text-gray-900 dark:text-white">Änderung</th>
                          <th className="border border-gray-200 dark:border-gray-600 p-2 text-center font-semibold text-gray-900 dark:text-white">Bestand</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exportData.memberStats.map((stat, index) => {
                          const change = stat.eintritte - stat.austritte;
                          return (
                            <tr key={stat.year} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}>
                              <td className="border border-gray-200 dark:border-gray-600 p-2 font-medium text-gray-900 dark:text-white">{stat.year}</td>
                              <td className="border border-gray-200 dark:border-gray-600 p-2 text-center text-success-600 font-medium">+{stat.eintritte}</td>
                              <td className="border border-gray-200 dark:border-gray-600 p-2 text-center text-error-500 font-medium">{stat.austritte > 0 ? `-${stat.austritte}` : '0'}</td>
                              <td className={`border border-gray-200 dark:border-gray-600 p-2 text-center font-bold ${change >= 0 ? 'text-success-600' : 'text-error-500'}`}>
                                {change >= 0 ? `+${change}` : change}
                              </td>
                              <td className="border border-gray-200 dark:border-gray-600 p-2 text-center font-bold text-info-600">{stat.totalMembers}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Statistik Tab - Alters-, Geschlechter-, PLZ-Verteilung */}
                {activeTab === 'statistics' && statistics && (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Detaillierte Mitgliederstatistiken - Altersverteilung, Geschlecht, Postleitzahlen und Eintrittsmonate.
                    </p>

                    {/* Grid mit 4 Charts */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                      {/* Altersverteilung */}
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Altersverteilung</h4>
                        <div style={{ height: '200px', width: '100%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={Object.entries(statistics.ageGroups).map(([group, count]) => ({ gruppe: group, anzahl: count }))}
                              margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis dataKey="gruppe" tick={{ fontSize: 10, fill: '#666' }} />
                              <YAxis tick={{ fontSize: 10, fill: '#666' }} />
                              <Tooltip />
                              <Bar dataKey="anzahl" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Mitglieder" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Geschlechterverteilung */}
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Geschlechterverteilung</h4>
                        <div style={{ height: '200px', width: '100%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={Object.entries(statistics.genderStats).map(([gender, count]) => ({
                                geschlecht: gender === 'M' ? 'Männlich' : gender === 'W' ? 'Weiblich' : 'Unbekannt',
                                anzahl: count
                              }))}
                              margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis dataKey="geschlecht" tick={{ fontSize: 10, fill: '#666' }} />
                              <YAxis tick={{ fontSize: 10, fill: '#666' }} />
                              <Tooltip />
                              <Bar dataKey="anzahl" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Mitglieder" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* PLZ-Verteilung (Top 10) */}
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">PLZ-Verteilung (Top 10)</h4>
                        <div style={{ height: '200px', width: '100%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={statistics.plzStats.map(([plz, count]) => ({ plz, anzahl: count }))}
                              margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                              layout="vertical"
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis type="number" tick={{ fontSize: 10, fill: '#666' }} />
                              <YAxis dataKey="plz" type="category" tick={{ fontSize: 10, fill: '#666' }} width={60} />
                              <Tooltip />
                              <Bar dataKey="anzahl" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Mitglieder" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Eintrittsmonate */}
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Eintritte nach Monat</h4>
                        <div style={{ height: '200px', width: '100%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={Object.entries(statistics.entryMonths).map(([monat, count]) => ({ monat, anzahl: count }))}
                              margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis dataKey="monat" tick={{ fontSize: 9, fill: '#666' }} />
                              <YAxis tick={{ fontSize: 10, fill: '#666' }} />
                              <Tooltip />
                              <Bar dataKey="anzahl" fill="#10b981" radius={[4, 4, 0, 0]} name="Eintritte" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>

                    {/* Zusammenfassungstabelle */}
                    <StatGroup columns={4} className="mt-4">
                      <StatCard icon={UserGroupIcon} tone="primary" valueTone="primary" label="Gesamt" value={filteredMembers.length} />
                      <StatCard icon={GenderIcon} tone="info" valueTone="info" label="Männlich" value={statistics.genderStats.M} />
                      <StatCard icon={GenderIcon} tone="error" valueTone="error" label="Weiblich" value={statistics.genderStats.W} />
                      <StatCard icon={MapPinIcon} tone="warning" valueTone="warning" label="PLZ-Bereiche" value={statistics.plzStats.length} />
                    </StatGroup>
                  </div>
                )}

                {/* Termine Tab - Geburtstage, Jubiläen, Ehrungen */}
                {activeTab === 'events' && statistics && (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Anstehende Geburtstage, Mitgliedsjubiläen und Ehrungen für langjährige Mitglieder.
                    </p>

                    {/* 3 Tabellen nebeneinander */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                      {/* Geburtstage */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                          🎂 Nächste Geburtstage
                        </h4>
                        <div className="max-h-[300px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-gray-100 dark:bg-gray-600">
                              <tr>
                                <th className="p-2 text-left">Name</th>
                                <th className="p-2 text-center">Datum</th>
                                <th className="p-2 text-center">Alter</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statistics.upcomingBirthdays.map((m, i) => (
                                <tr key={m.mg_nr} className={i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}>
                                  <td className="p-2 text-gray-900 dark:text-white">{m.vorname} {m.name}</td>
                                  <td className="p-2 text-center text-gray-600 dark:text-gray-300">
                                    {m.nextBirthday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                  </td>
                                  <td className="p-2 text-center font-medium text-info-600">{m.age + 1}</td>
                                </tr>
                              ))}
                              {statistics.upcomingBirthdays.length === 0 && (
                                <tr><td colSpan={3} className="p-4 text-center text-gray-500">Keine Daten</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Jubiläen */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                          🏆 Jubiläen dieses Jahr
                        </h4>
                        <div className="max-h-[300px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-gray-100 dark:bg-gray-600">
                              <tr>
                                <th className="p-2 text-left">Name</th>
                                <th className="p-2 text-center">Jahre</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statistics.jubilees.map((m, i) => (
                                <tr key={m.mg_nr} className={i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}>
                                  <td className="p-2 text-gray-900 dark:text-white">{m.vorname} {m.name}</td>
                                  <td className="p-2 text-center">
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                      m.membershipYears >= 25 ? 'bg-warning-100 text-warning-700' :
                                      m.membershipYears >= 20 ? 'bg-primary-100 text-primary-700' :
                                      m.membershipYears >= 10 ? 'bg-info-100 text-info-700' :
                                      'bg-success-100 text-success-700'
                                    }`}>
                                      {m.membershipYears} Jahre
                                    </span>
                                  </td>
                                </tr>
                              ))}
                              {statistics.jubilees.length === 0 && (
                                <tr><td colSpan={2} className="p-4 text-center text-gray-500">Keine Jubiläen in diesem Jahr</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Ehrungen (20+ Jahre) */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                          🎖️ Ehrenmitglieder
                        </h4>
                        <div className="max-h-[300px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-gray-100 dark:bg-gray-600">
                              <tr>
                                <th className="p-2 text-left">Name</th>
                                <th className="p-2 text-center">Eintritt</th>
                                <th className="p-2 text-center">Jahre</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statistics.honors.map((m, i) => (
                                <tr key={m.mg_nr} className={i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}>
                                  <td className="p-2 text-gray-900 dark:text-white">{m.vorname} {m.name}</td>
                                  <td className="p-2 text-center text-gray-600 dark:text-gray-300">{formatDate(m.eintritt)}</td>
                                  <td className="p-2 text-center font-bold text-warning-600">
                                    {m.membershipYears >= 30 ? '🌟 ' : ''}{m.membershipYears}
                                  </td>
                                </tr>
                              ))}
                              {statistics.honors.length === 0 && (
                                <tr><td colSpan={3} className="p-4 text-center text-gray-500">Keine Ehrenmitglieder</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Zusammenfassung */}
                    <StatGroup columns={3} className="mt-4">
                      <StatCard icon={CakeIcon} tone="info" valueTone="info" label="Geburtstage (nächste 3 Mon.)" value={statistics.upcomingBirthdays.length} />
                      <StatCard icon={TrophyIcon} tone="success" valueTone="success" label="Jubiläen dieses Jahr" value={statistics.jubilees.length} />
                      <StatCard icon={StarIcon} tone="warning" valueTone="warning" label="Ehrenmitglieder" value={statistics.honors.length} />
                    </StatGroup>
                  </div>
                )}

                {/* Hunde+ Tab - Erweiterte Hundeliste */}
                {activeTab === 'dogs-extended' && dogsExtended && (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Komplette Hundeliste mit Impfstatus und Versicherungsübersicht. Probleme werden hervorgehoben.
                    </p>

                    {/* Status-Karten */}
                    <StatGroup columns={4} className="mb-4">
                      <StatCard icon={DogIcon} tone="success" valueTone="success" label="Hunde gesamt" value={dogsExtended.allDogs.length} />
                      <StatCard icon={ExclamationTriangleIcon} tone="error" valueTone="error" label="Tollwut abgelaufen" value={dogsExtended.expiredTollwut.length} />
                      <StatCard icon={ClockIcon} tone="warning" valueTone="warning" label="Läuft bald ab" value={dogsExtended.expiringSoonTollwut.length} />
                      <StatCard icon={ShieldExclamationIcon} tone="warning" valueTone="warning" label="Ohne Versicherung" value={dogsExtended.noInsurance.length} />
                    </StatGroup>

                    {/* Tabellen in Tabs */}
                    <div className="space-y-4">
                      {/* Alle Hunde */}
                      <details className="bg-gray-50 dark:bg-gray-700/50 rounded-xl" open>
                        <summary className="p-4 cursor-pointer font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                          🐕 Alle Hunde ({dogsExtended.allDogs.length})
                        </summary>
                        <div className="p-4 pt-0 max-h-[300px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-gray-100 dark:bg-gray-600">
                              <tr>
                                <th className="p-2 text-left">Besitzer</th>
                                <th className="p-2 text-left">Hundename</th>
                                <th className="p-2 text-left">Versicherung</th>
                                <th className="p-2 text-center">Tollwut bis</th>
                                <th className="p-2 text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dogsExtended.allDogs.map((dog, i) => (
                                <tr key={i} className={`${
                                  dog.isExpired ? 'bg-error-50 dark:bg-error-900/20' :
                                  dog.isExpiringSoon ? 'bg-warning-50 dark:bg-warning-900/20' :
                                  !dog.hasInsurance ? 'bg-warning-50 dark:bg-warning-900/20' :
                                  i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'
                                }`}>
                                  <td className="p-2 text-gray-900 dark:text-white">{dog.memberVorname} {dog.memberName}</td>
                                  <td className="p-2 font-medium text-gray-900 dark:text-white">{dog.dogName}</td>
                                  <td className="p-2 text-gray-600 dark:text-gray-300">{dog.versicherung || '-'}</td>
                                  <td className="p-2 text-center text-gray-600 dark:text-gray-300">{formatDate(dog.tollwutBis)}</td>
                                  <td className="p-2 text-center">
                                    {dog.isExpired ? (
                                      <span className="px-2 py-1 bg-error-100 text-error-700 rounded-full text-xs">Abgelaufen</span>
                                    ) : dog.isExpiringSoon ? (
                                      <span className="px-2 py-1 bg-warning-100 text-warning-700 rounded-full text-xs">Bald fällig</span>
                                    ) : !dog.hasInsurance ? (
                                      <span className="px-2 py-1 bg-warning-100 text-warning-700 rounded-full text-xs">Keine Vers.</span>
                                    ) : (
                                      <span className="px-2 py-1 bg-success-100 text-success-700 rounded-full text-xs">OK</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>

                      {/* Abgelaufene Tollwut */}
                      {dogsExtended.expiredTollwut.length > 0 && (
                        <details className="bg-error-50 dark:bg-error-900/20 rounded-xl">
                          <summary className="p-4 cursor-pointer font-semibold text-error-700 dark:text-error-300 flex items-center gap-2">
                            ⚠️ Abgelaufene Tollwutimpfung ({dogsExtended.expiredTollwut.length})
                          </summary>
                          <div className="p-4 pt-0 max-h-[200px] overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead className="sticky top-0 bg-error-100 dark:bg-error-900/50">
                                <tr>
                                  <th className="p-2 text-left">Besitzer</th>
                                  <th className="p-2 text-left">Hundename</th>
                                  <th className="p-2 text-center">Abgelaufen am</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dogsExtended.expiredTollwut.map((dog, i) => (
                                  <tr key={i} className="bg-white dark:bg-gray-800">
                                    <td className="p-2 text-gray-900 dark:text-white">{dog.memberVorname} {dog.memberName}</td>
                                    <td className="p-2 font-medium text-gray-900 dark:text-white">{dog.dogName}</td>
                                    <td className="p-2 text-center text-error-600 font-medium">{formatDate(dog.tollwutBis)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      )}

                      {/* Bald ablaufende Tollwut */}
                      {dogsExtended.expiringSoonTollwut.length > 0 && (
                        <details className="bg-warning-50 dark:bg-warning-900/20 rounded-xl">
                          <summary className="p-4 cursor-pointer font-semibold text-warning-700 dark:text-warning-300 flex items-center gap-2">
                            ⏰ Tollwut läuft in 30 Tagen ab ({dogsExtended.expiringSoonTollwut.length})
                          </summary>
                          <div className="p-4 pt-0 max-h-[200px] overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead className="sticky top-0 bg-warning-100 dark:bg-warning-900/50">
                                <tr>
                                  <th className="p-2 text-left">Besitzer</th>
                                  <th className="p-2 text-left">Hundename</th>
                                  <th className="p-2 text-center">Gültig bis</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dogsExtended.expiringSoonTollwut.map((dog, i) => (
                                  <tr key={i} className="bg-white dark:bg-gray-800">
                                    <td className="p-2 text-gray-900 dark:text-white">{dog.memberVorname} {dog.memberName}</td>
                                    <td className="p-2 font-medium text-gray-900 dark:text-white">{dog.dogName}</td>
                                    <td className="p-2 text-center text-warning-600 font-medium">{formatDate(dog.tollwutBis)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      )}

                      {/* Ohne Versicherung */}
                      {dogsExtended.noInsurance.length > 0 && (
                        <details className="bg-warning-50 dark:bg-warning-900/20 rounded-xl">
                          <summary className="p-4 cursor-pointer font-semibold text-warning-700 dark:text-warning-300 flex items-center gap-2">
                            🔔 Ohne gültige Versicherung ({dogsExtended.noInsurance.length})
                          </summary>
                          <div className="p-4 pt-0 max-h-[200px] overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead className="sticky top-0 bg-warning-100 dark:bg-warning-900/50">
                                <tr>
                                  <th className="p-2 text-left">Besitzer</th>
                                  <th className="p-2 text-left">Hundename</th>
                                  <th className="p-2 text-left">Versicherung</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dogsExtended.noInsurance.map((dog, i) => (
                                  <tr key={i} className="bg-white dark:bg-gray-800">
                                    <td className="p-2 text-gray-900 dark:text-white">{dog.memberVorname} {dog.memberName}</td>
                                    <td className="p-2 font-medium text-gray-900 dark:text-white">{dog.dogName}</td>
                                    <td className="p-2 text-warning-600">{dog.versicherung || 'Keine angegeben'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                )}
              </div>

            </>
          ) : null}
    </ModalShell>
  );
};

export default MemberExcelExportModal;
